import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { useToast, type ToastLevel } from '../components/ToastProvider';
import { useAppSelector } from '../app/store';

export interface NotificationRow {
  id: number;
  event_type: string;
  title: string;
  body: string | null;
  level: ToastLevel;
  is_read: number;
  created_at: string;
}

/** Poll cadence for the in-app notification feed. */
const POLL_MS = 8_000;
const seenKey = (userId: number) => `notif:lastSeen:${userId}`;

/**
 * Polls the notification feed and pops a toast for each newly-arrived item, so a
 * client sees approval/reject/fill events the moment an admin acts — without a
 * page refresh. The highest id already shown is persisted per-user in
 * localStorage, so a reload never re-toasts the backlog. Returns the latest feed
 * (items + unread count) for any UI that wants to render a list/badge.
 *
 * Mount once near the app root for an authenticated user.
 */
export function useNotificationsPoller() {
  const toast = useToast();
  const user = useAppSelector((s) => s.auth.user);
  const accessToken = useAppSelector((s) => s.auth.accessToken);
  const userId = user?.id ?? 0;
  // Tracks whether we've established the baseline (the backlog present at mount,
  // which must NOT be toasted). Reset whenever the logged-in user changes.
  const baselineReady = useRef(false);

  useEffect(() => { baselineReady.current = false; }, [userId]);

  const query = useQuery({
    queryKey: ['notifications', userId],
    queryFn: async () => (await api.get('/notifications')).data.data as { items: NotificationRow[]; unread: number },
    enabled: !!accessToken && !!userId,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!userId || !query.data) return;
    const items = query.data.items ?? [];
    if (!items.length) return;

    const maxId = items[0].id; // feed is newest-first

    // First successful poll for this user establishes the baseline silently: never
    // toast the backlog already present at mount. Advance the marker to the current
    // max so the next poll only compares against what arrives from here on.
    if (!baselineReady.current) {
      baselineReady.current = true;
      localStorage.setItem(seenKey(userId), String(maxId));
      return;
    }

    const lastSeen = Number(localStorage.getItem(seenKey(userId)) ?? '0');
    // Oldest-first so a burst toasts in chronological order.
    const fresh = items.filter((n) => n.id > lastSeen).sort((a, b) => a.id - b.id);
    for (const n of fresh) {
      toast.show(n.body || n.title, { level: n.level || 'info', title: n.body ? n.title : undefined });
    }
    if (maxId > lastSeen) localStorage.setItem(seenKey(userId), String(maxId));
  }, [query.data, userId, toast]);

  return query;
}
